import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Mail, ArrowRight } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Reveal, CountUp, Eyebrow, PrimaryLink, OutlineLink, MaskedLine } from "@/components/shared";
import { programs, beyondPrograms, ADDRESS, EMAIL } from "@/data/content";
import { useLang } from "@/i18n";

const EASE = [0.22, 1, 0.36, 1];

const Hero = () => {
  const [offset, setOffset] = useState(0);
  const { t } = useLang();
  const reduce = useReducedMotion();

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const onScroll = () => setOffset(window.scrollY * 0.22);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <section className="relative bg-charcoal text-white overflow-hidden min-h-[calc(100svh-84px)] flex items-center" data-testid="hero-section">
      <div className="absolute inset-0" aria-hidden="true">
        <img
          src="/assets/hero-temple.png"
          alt=""
          className="w-full h-[115%] object-cover object-center"
          style={{ transform: `translateY(${-offset}px) scale(1.05)`, filter: "blur(6px) brightness(0.85) saturate(1.02)" }}
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(90deg, rgba(23, 18, 13, 0.88) 0%, rgba(23, 18, 13, 0.50) 50%, rgba(23, 18, 13, 0.25) 100%), linear-gradient(to top, rgba(23, 18, 13, 0.55) 0%, rgba(23, 18, 13, 0.12) 42%, rgba(23, 18, 13, 0) 100%)",
          }}
        />
      </div>
      <div className="relative max-w-7xl mx-auto px-5 lg:px-8 py-20 sm:py-24 lg:py-32 w-full">
        <motion.p
          className="eyebrow eyebrow-on-dark mb-4 text-[11px] sm:text-[12.5px]"
          initial={reduce ? false : { opacity: 0, y: 16 }}
          animate={reduce ? undefined : { opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1, ease: EASE }}
        >
          {t("hero.eyebrow")}
        </motion.p>
        <h1 className="text-[40px] leading-[1.08] sm:text-6xl lg:text-7xl font-semibold text-white max-w-4xl" data-testid="hero-heading">
          <MaskedLine delay={0.28}>
            {t("hero.title1")}
            <span className="text-saffron-light">{t("hero.titleAccent")}</span>
          </MaskedLine>
          <span className="sr-only"> </span>
          <MaskedLine delay={0.46}>{t("hero.title2")}</MaskedLine>
        </h1>
        <motion.p
          className="mt-7 text-base lg:text-lg text-[rgba(255,255,255,0.88)] max-w-2xl leading-relaxed"
          data-testid="hero-intro"
          initial={reduce ? false : { opacity: 0, y: 22 }}
          animate={reduce ? undefined : { opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.64, ease: EASE }}
        >
          {t("hero.intro")}
        </motion.p>
        <motion.div
          className="mt-10 flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-4"
          initial={reduce ? false : { opacity: 0, y: 22 }}
          animate={reduce ? undefined : { opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.78, ease: EASE }}
        >
          <PrimaryLink to="/our-work" testId="hero-support-mission-button" className="w-full sm:w-auto">{t("hero.cta1")}</PrimaryLink>
          <OutlineLink to="/dhamma" testId="hero-explore-work-button" className="w-full sm:w-auto">{t("hero.cta2")}</OutlineLink>
        </motion.div>
        <motion.p
          className="mt-14 font-sans text-[12px] uppercase tracking-[0.32em] text-[rgba(255,255,255,0.65)]"
          data-testid="hero-values"
          initial={reduce ? false : { opacity: 0 }}
          animate={reduce ? undefined : { opacity: 1 }}
          transition={{ duration: 1, delay: 0.95, ease: EASE }}
        >
          {t("hero.values")}
        </motion.p>
      </div>
    </section>
  );
};

const impactBarStats = [
  { value: "30,000+", label: "KM Padayatra" },
  { value: "3", label: "Flagship Programs" },
  { value: "2026", label: "Active & Growing" },
  { value: "Reg: BK 4 IV 59/12-13", label: "Karnataka Registered Charitable Trust" },
];

const ImpactBar = () => (
  <section className="bg-ivory border-b border-[rgba(41,39,34,0.1)]" data-testid="impact-bar-section">
    <div className="max-w-7xl mx-auto px-5 lg:px-8 py-12 grid grid-cols-2 lg:grid-cols-4 gap-8">
      {impactBarStats.map((s, i) => (
        <Reveal key={s.label} delay={i * 80}>
          <div data-testid={`impact-bar-stat-${i}`}>
            <p className={`font-serif font-semibold text-saffron ${i === 3 ? "text-lg lg:text-xl" : "text-3xl lg:text-4xl"}`}>{s.value}</p>
            <p className="mt-2 text-sm text-bodytext/75 leading-snug">{s.label}</p>
          </div>
        </Reveal>
      ))}
    </div>
  </section>
);

const marqueeItems = [
  "Compassion in Action",
  "Dana — the Gift of Giving",
  "Karuna for All Beings",
  "Wisdom of the Dhamma",
  "Seva — Selfless Service",
  "Peace Begins Within",
];

const EditorialMarquee = () => (
  <section className="bg-charcoal border-y border-[rgba(245,239,228,0.14)] overflow-hidden" data-testid="editorial-marquee" aria-hidden="true">
    <div className="marquee-track flex items-center whitespace-nowrap py-7">
      {[0, 1].map((dup) => (
        <div key={dup} className="flex shrink-0 items-center">
          {marqueeItems.map((item) => (
            <span key={`${dup}-${item}`} className="flex items-center">
              <span className="font-serif italic text-[26px] lg:text-[34px] leading-none text-[rgba(245,239,228,0.85)] px-8">{item}</span>
              <span className="w-2 h-2 rounded-full bg-saffron shrink-0" />
            </span>
          ))}
        </div>
      ))}
    </div>
  </section>
);

const ProgramCard = ({ p, i }) => {
  const { lang, t } = useLang();
  const kn = lang === "kn" ? p.kn : null;
  return (
    <Reveal delay={i * 90} className="h-full">
      <Link
        to={`/our-work/${p.id}`}
        className="group card-ivory !p-0 overflow-hidden h-full flex flex-col no-underline block text-bodytext"
        data-testid={`program-card-${p.id}`}
      >
        <div className="img-zoom aspect-[4/3] flex items-center justify-center p-6" style={{ backgroundColor: p.emblemBg }}>
          <img src={p.emblem} alt={`${p.name} emblem`} className="max-w-full max-h-full object-contain" loading="lazy" />
        </div>
        <div className="p-7 flex flex-col flex-1">
          <div className="flex items-baseline justify-between gap-3">
            <p className="font-sans text-[11px] font-extrabold uppercase tracking-[0.2em] text-saffron">{t("work.tag")}</p>
            <span className="font-serif text-[15px] font-semibold text-saffron/60" aria-hidden="true">{String(i + 1).padStart(2, "0")}</span>
          </div>
          <h3 className="mt-3 font-serif text-[26px] font-semibold leading-tight text-charcoal">
            {kn ? `${p.knName} — ${kn.tagline}` : `${p.name} — ${p.tagline}`}
          </h3>
          <p className="mt-3 text-[15px] leading-relaxed text-bodytext/75 flex-1">{kn ? kn.desc : p.desc}</p>
          <span className="link-arrow mt-5 text-[14px]">
            {t("common.learnMore")}
          </span>
        </div>
      </Link>
    </Reveal>
  );
};

const OurWorkSection = () => {
  const { lang, t } = useLang();
  return (
    <section className="bg-ivory" data-testid="our-work-section">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-20 lg:py-28">
        <Reveal>
          <Eyebrow>{t("work.eyebrow")}</Eyebrow>
          <h2 className="text-[34px] sm:text-4xl lg:text-5xl font-semibold text-charcoal max-w-2xl">{t("work.heading")}</h2>
          <p className="mt-4 font-serif italic text-lg text-bodytext/70 max-w-2xl">{t("work.subhead")}</p>
        </Reveal>
        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-7">
          {programs.map((p, i) => (
            <ProgramCard key={p.id} p={p} i={i} />
          ))}
        </div>

        <Reveal className="mt-20">
          <h3 className="font-serif text-2xl lg:text-3xl font-semibold text-charcoal">{t("work.beyondHeading")}</h3>
          <p className="mt-3 text-bodytext/75 max-w-2xl">{t("work.beyondIntro")}</p>
          <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-4">
            {beyondPrograms.filter((b) => b.id !== "nalanda-study-centre").map((b) => (
              <Link
                key={b.id}
                to={`/our-work/${b.id}`}
                className="card-ivory text-bodytext p-5 flex items-center gap-3 no-underline"
                data-testid={`beyond-program-${b.id}`}
              >
                <b.icon size={22} strokeWidth={1.5} className="text-saffron shrink-0" aria-hidden="true" />
                <span className="font-sans font-semibold text-[14px] leading-snug">{lang === "kn" ? b.knLabel : b.label}</span>
              </Link>
            ))}
          </div>
          <div className="mt-10">
            <PrimaryLink to="/our-work" testId="explore-all-work-button">{t("work.explore")}</PrimaryLink>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

const impactStats = [
  { end: 30000, suffix: "+", label: "KM Padayatra Walked" },
  { end: 25, suffix: "", label: "Acres of Land" },
  { end: 1000, suffix: "+", label: "Phase-1 Capacity" },
  { end: 17, suffix: "+", label: "Years of Service" },
];

const ImpactStats = () => (
  <section className="text-white" style={{ backgroundColor: "#1C1C1E" }} data-testid="impact-stats-section">
    <div className="max-w-7xl mx-auto px-5 lg:px-8 py-20 lg:py-28">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 items-center">
        <Reveal className="lg:col-span-5">
          <Eyebrow dark>Our Impact</Eyebrow>
          <h2 className="text-[34px] sm:text-4xl lg:text-5xl font-semibold text-white">Real People. Lasting Change.</h2>
          <p className="mt-5 text-[rgba(255,255,255,0.78)] leading-relaxed">
            Through dedicated effort and the generosity of supporters, IBMCT continues to create meaningful, lasting change in the
            lives of those it serves.
          </p>
          <div className="mt-8 img-zoom !rounded-2xl aspect-[16/10] relative overflow-hidden bg-charcoal">
            <img
              src="/assets/gallery/g2.webp"
              alt=""
              aria-hidden="true"
              className="absolute inset-0 w-full h-full object-cover"
              style={{ filter: "blur(22px) brightness(0.72) saturate(0.92)", transform: "scale(1.15)" }}
              loading="lazy"
            />
            <img
              src="/assets/gallery/g2.webp"
              alt="A large gathering of IBMCT monastics and community members in seated prayer"
              className="relative w-full h-full object-contain"
              loading="lazy"
              data-testid="impact-ceremony-image"
            />
          </div>
        </Reveal>
        <div className="lg:col-span-7 grid grid-cols-2 gap-x-8 gap-y-12">
          {impactStats.map((s, i) => (
            <Reveal key={s.label} delay={i * 100}>
              <div className="border-l-2 border-saffron pl-6" data-testid={`impact-stat-${i}`}>
                <p className="font-serif text-5xl lg:text-6xl font-semibold text-saffron-light">
                  <CountUp end={s.end} suffix={s.suffix} testId={`impact-stat-value-${i}`} />
                </p>
                <p className="mt-3 font-sans font-semibold text-[rgba(255,255,255,0.85)] tracking-wide text-[15px]">{s.label}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </div>
  </section>
);

const JourneySection = () => (
  <section className="relative overflow-hidden bg-sandbg" data-testid="journey-section">
    <div className="relative z-10 max-w-7xl mx-auto px-5 lg:px-8 py-20 lg:py-28">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
        <Reveal className="lg:col-span-5">
          <Eyebrow>Journey</Eyebrow>
          <h2 className="text-[34px] sm:text-4xl lg:text-5xl font-semibold text-charcoal leading-[1.08]" data-testid="journey-heading">
            Homes, Shelters and New Beginnings
          </h2>
          <div className="mt-6 w-14 h-px bg-saffron" aria-hidden="true" />
        </Reveal>
        <Reveal className="lg:col-span-7" delay={100}>
          <p className="text-[17px] leading-relaxed text-bodytext/85" data-testid="journey-body-1">
            Each flagship initiative addresses a fundamental human need — a home for elders, safety and dignity for women, and a
            nurturing beginning for children.
          </p>
          <p className="mt-5 text-[17px] leading-relaxed text-bodytext/85" data-testid="journey-body-2">
            These three transformative projects are being unveiled together at Maha Karuna 2026, under one shared resolve: to create
            spaces where every individual can live with dignity, security, compassion and hope.
          </p>
          <p className="mt-5 text-[17px] leading-relaxed text-bodytext/85" data-testid="journey-body-lykit">
            IBMCT (International Buddhist Monks Charitable Trust), in collaboration with Lykit Entertainment Pvt. Ltd., has taken this
            initiative forward with the belief that meaningful change begins when compassion becomes action.
          </p>
          <p className="mt-7 font-serif italic text-xl lg:text-2xl text-charcoal/90 border-l-2 border-saffron pl-6" data-testid="journey-body-quote">
            Because dignity is not a privilege — it is a fundamental right.
          </p>
        </Reveal>
      </div>
    </div>
  </section>
);

const mediaImages = [
  { src: "/assets/gallery/g4.webp", alt: "IBMCT monastics seated in chanting and prayer" },
  { src: "/assets/gallery/g1.webp", alt: "Monks standing in procession with offerings" },
  { src: "/assets/gallery/g6.webp", alt: "Lamp-lighting ceremony with monastics and dignitaries" },
  { src: "/assets/gallery/g3.webp", alt: "Dignitaries lighting the ceremonial lamp at an IBMCT event" },
];

const MediaSection = () => (
  <section className="bg-ivory" data-testid="media-section">
    <div className="max-w-7xl mx-auto px-5 lg:px-8 py-20 lg:py-28">
      <Reveal>
        <Eyebrow>Media &amp; Gallery</Eyebrow>
        <h2 className="text-[34px] sm:text-4xl lg:text-5xl font-semibold text-charcoal">Moments of Seva</h2>
      </Reveal>
      <div className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-4">
        {mediaImages.map((img, i) => (
          <Reveal key={i} delay={i * 70}>
            <Link to="/media/photos" className="group block" data-testid={`media-featured-image-${i}`}>
              <div className="img-zoom !rounded-2xl aspect-square">
                <img src={img.src} alt={img.alt} className="w-full h-full object-cover" loading="lazy" />
              </div>
            </Link>
          </Reveal>
        ))}
      </div>
      <Reveal className="mt-10 flex flex-wrap gap-4">
        <PrimaryLink to="/media/photos" testId="media-gallery-button">View Photo Gallery</PrimaryLink>
        <OutlineLink to="/media/videos" testId="media-videos-button">Watch Videos &amp; Sermons</OutlineLink>
      </Reveal>
    </div>
  </section>
);

const QuoteBanner = () => (
  <section className="bg-coal text-white" data-testid="quote-banner-section">
    <div className="max-w-4xl mx-auto px-5 lg:px-8 py-20 lg:py-28 text-center">
      <Reveal>
        <div className="thin-sep w-16 mx-auto mb-10" aria-hidden="true" />
        <blockquote className="font-serif text-[26px] sm:text-3xl lg:text-4xl leading-snug text-white" data-testid="quote-banner-text">
          “True compassion is not just feeling for others — it is being the change you wish to see in the world.”
        </blockquote>
        <p className="mt-8 font-sans text-sm tracking-[0.18em] uppercase text-saffron-light">— Lord Buddha</p>
      </Reveal>
    </div>
  </section>
);

const journeyPaths = [
  { key: "learn", to: "/dhamma" },
  { key: "practice", to: "/nalanda" },
  { key: "serve", to: "/volunteer" },
  { key: "give", to: "/donate" },
  { key: "visit", to: "/contact" },
];

const DonateCTA = () => {
  const { t } = useLang();
  return (
    <section className="relative bg-charcoal text-white overflow-hidden" data-testid="donate-cta-section">
      <div className="absolute inset-0" aria-hidden="true">
        <img src="/assets/gallery/g6.webp" alt="" className="w-full h-full object-cover opacity-20" loading="lazy" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(to bottom, rgba(23, 18, 13,0.92) 0%, rgba(23, 18, 13,0.88) 100%)" }} />
      </div>
      <div className="relative max-w-7xl mx-auto px-5 lg:px-8 py-24 lg:py-32">
        <Reveal>
          <Eyebrow dark>{t("journey.eyebrow")}</Eyebrow>
          <h2 className="text-[38px] sm:text-5xl lg:text-6xl font-semibold text-white max-w-3xl">{t("journey.heading")}</h2>
          <p className="mt-6 text-[rgba(255,255,255,0.78)] max-w-2xl leading-relaxed text-base lg:text-lg">
            {t("journey.intro")}
          </p>
        </Reveal>
        <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5">
          {journeyPaths.map((p, i) => (
            <Reveal key={p.key} delay={i * 80}>
              <Link
                to={p.to}
                className="group flex flex-col h-full rounded-2xl border border-[rgba(245,239,228,0.14)] bg-[rgba(245,239,228,0.03)] p-7 no-underline hover:border-saffron/60 hover:bg-[rgba(201,138,46,0.08)] transition-colors duration-300"
                data-testid={`journey-path-${p.key}`}
              >
                <span className="font-serif text-[11px] font-semibold uppercase tracking-[0.24em] text-saffron-light/70">
                  0{i + 1}
                </span>
                <h3 className="mt-4 font-serif text-[24px] font-semibold text-white">{t(`journey.${p.key}.title`)}</h3>
                <p className="mt-3 font-sans text-[14.5px] leading-relaxed text-[rgba(245,239,228,0.72)] flex-1">
                  {t(`journey.${p.key}.body`)}
                </p>
                <span className="mt-5 font-sans text-[13px] font-semibold text-saffron-light group-hover:text-saffron transition-colors duration-200">
                  {t(`journey.${p.key}.cta`)} →
                </span>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const ContactTeaser = () => (
  <section className="relative overflow-hidden" data-testid="contact-teaser-section" style={{ backgroundColor: "#F4EBDD" }}>
    <div
      className="absolute inset-0 pointer-events-none"
      aria-hidden="true"
      style={{ background: "radial-gradient(ellipse at 15% 15%, rgba(212,168,83,0.20) 0%, rgba(244,235,221,0) 55%)" }}
    />
    <div className="relative z-10 max-w-7xl mx-auto px-5 lg:px-8 py-20 lg:py-24">
      <Reveal>
        <div
          className="rounded-3xl px-6 py-10 sm:px-10 lg:px-14 lg:py-12"
          data-testid="contact-teaser-card"
          style={{
            background: "rgba(255,255,255,0.55)",
            backdropFilter: "blur(16px) saturate(160%)",
            WebkitBackdropFilter: "blur(16px) saturate(160%)",
            border: "1px solid rgba(212,168,83,0.35)",
            boxShadow: "0 20px 50px -30px rgba(28,28,30,0.45)",
          }}
        >
          <h3 className="font-serif text-2xl lg:text-3xl font-semibold" style={{ color: "#D4A853" }}>Reach Out to IBMCT</h3>
          <p className="mt-4 max-w-2xl leading-relaxed" style={{ color: "#1C1C1E" }}>
            Whether you have a question, wish to volunteer, or want to support our work — we welcome you.
          </p>
          <div className="mt-7 space-y-4 text-[15px]" style={{ color: "#1C1C1E" }}>
            <p className="flex items-start gap-3" data-testid="contact-teaser-address">
              <MapPin size={20} strokeWidth={2} aria-hidden="true" style={{ color: "#D4A853", marginTop: 2, flexShrink: 0 }} />
              <span>{ADDRESS}</span>
            </p>
            <p className="flex items-center gap-3">
              <Mail size={20} strokeWidth={2} aria-hidden="true" style={{ color: "#D4A853", flexShrink: 0 }} />
              <a href={`mailto:${EMAIL}`} className="no-underline hover:underline" style={{ color: "#1C1C1E" }} data-testid="contact-teaser-email">
                {EMAIL}
              </a>
            </p>
          </div>
          <div className="mt-9 flex flex-wrap gap-4">
            <PrimaryLink to="/contact" testId="contact-us-button">Contact Us</PrimaryLink>
            <Link
              to="/volunteer"
              className="btn"
              data-testid="volunteer-with-us-button"
              style={{ backgroundColor: "transparent", border: "1px solid rgba(28,28,30,0.35)", color: "#1C1C1E" }}
            >
              Volunteer With Us
              <ArrowRight size={17} aria-hidden="true" />
            </Link>
          </div>
        </div>
      </Reveal>
    </div>
    <div
      className="absolute bottom-0 left-0 right-0 h-28 pointer-events-none z-0"
      aria-hidden="true"
      style={{ background: "linear-gradient(180deg, rgba(244,235,221,0) 0%, #17120D 100%)" }}
    />
  </section>
);

const SeamFade = ({ from, to }) => (
  <div
    aria-hidden="true"
    style={{ height: "100px", background: `linear-gradient(to bottom, ${from} 0%, ${to} 100%)` }}
  />
);

const Home = () => (
  <>
    <Hero />
    <SeamFade from="#17120D" to="#F5EFE4" />
    <ImpactBar />
    <SeamFade from="#F5EFE4" to="#17120D" />
    <EditorialMarquee />
    <SeamFade from="#17120D" to="#F5EFE4" />
    <OurWorkSection />
    <SeamFade from="#F5EFE4" to="#1C1C1E" />
    <ImpactStats />
    <SeamFade from="#1C1C1E" to="#F3EBDD" />
    <JourneySection />
    <SeamFade from="#F3EBDD" to="#F5EFE4" />
    <MediaSection />
    <SeamFade from="#F5EFE4" to="#241A12" />
    <QuoteBanner />
    <DonateCTA />
    <SeamFade from="#17120D" to="#F4EBDD" />
    <ContactTeaser />
  </>
);

export default Home;
